import { apiClient } from '../client'
import { OWNS_ERRORS } from '../ownsErrors'
import type {
  BulkResponse,
  BulkWrite,
  IssueConfig,
  IssueDetail,
  IssueFacets,
  IssueListParams,
  IssueListResponse,
  IssuePatch,
  IssuePriority,
  IssueTimelineResponse,
  IssueUser,
  PriorityWrite,
  SavedView,
  SavedViewsResponse,
  SlaPolicyRow,
  StateWrite,
} from '../../app/modules/types/issues'

/**
 * The Issue API client — repointed to `/vulnerabilities/*` (handoff §2): one
 * issue is one finding, so there is no separate issue resource any more.
 *
 * Three things this file encodes that a caller must not re-decide:
 *
 * - **Page size is explicit.** The list and every kanban column ask for 25
 *   (NOT the repo's usual 10). There is no board endpoint: each column is its
 *   own list query with its own `state`. Anything above 100 is clamped
 *   server-side without a word.
 * - **Every single-issue POST returns the full detail**, re-read through the
 *   same annotated query — so there is no follow-up GET after a state move, an
 *   assign, a pin or a route. `patch` is the one exception and says so.
 * - **The board renders every error in place.** A path id that belongs to
 *   another organization is a 404, never a 403, and a role without
 *   `api:vul_logger:view` gets a real 403 on a page reachable on its feature flag
 *   alone. Every request here carries `OWNS_ERRORS`, so the page shows "no such
 *   issue" / <NotPermission/> itself instead of the global toast and the
 *   redirect to the dashboard. (A per-request flag, not an endpoint-list entry:
 *   these are the Vulnerabilities page's own paths, and a substring entry would
 *   silence its failures too.)
 */

/** Drop empties and stringify. `page_size` travels like any other param. */
function qs(params?: IssueListParams): string {
  if (!params) return ''
  const usp = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    usp.set(key, String(value))
  }
  return usp.toString()
}

const withQuery = (path: string, params?: IssueListParams) => {
  const search = qs(params)
  return search ? `${path}?${search}` : path
}

export const issuesApi = {
  getAll: async (params?: IssueListParams): Promise<IssueListResponse> =>
    (await apiClient.get(withQuery('/vulnerabilities/', params), OWNS_ERRORS)).data,
  getFacets: async (): Promise<IssueFacets> => (await apiClient.get('/vulnerabilities/filter/', OWNS_ERRORS)).data,
  getConfig: async (): Promise<IssueConfig> =>
    (await apiClient.get('/vulnerabilities/workflow-config/', OWNS_ERRORS)).data,
  getById: async (id: string): Promise<IssueDetail> =>
    (await apiClient.get(`/vulnerabilities/${id}/`, OWNS_ERRORS)).data,
  getTimeline: async (id: string, page = 1): Promise<IssueTimelineResponse> =>
    (await apiClient.get(`/vulnerabilities/${id}/timeline/?page=${page}&page_size=100`, OWNS_ERRORS)).data,
  setState: async (id: string, body: StateWrite): Promise<IssueDetail> =>
    (await apiClient.post(`/vulnerabilities/${id}/state/`, body, OWNS_ERRORS)).data,
  assign: async (id: string, assignee: string | null): Promise<IssueDetail> =>
    (await apiClient.post(`/vulnerabilities/${id}/assign/`, { assignee }, OWNS_ERRORS)).data,
  setPriority: async (id: string, body: PriorityWrite): Promise<IssueDetail> =>
    (await apiClient.post(`/vulnerabilities/${id}/priority/`, body, OWNS_ERRORS)).data,
  putBack: async (id: string): Promise<IssueDetail> =>
    (await apiClient.post(`/vulnerabilities/${id}/put-back/`, undefined, OWNS_ERRORS)).data,
  /** "Merge" = mark as duplicate: a won't-fix with reason `duplicate` (§5.8). */
  markDuplicate: async (
    id: string,
    body: { duplicate_of: string; justification: string }
  ): Promise<IssueDetail> =>
    (
      await apiClient.post(`/vulnerabilities/${id}/state/`, {
        state: 'wont_fix',
        reason: 'duplicate',
        ...body,
      }, OWNS_ERRORS)
    ).data,
  bulk: async (body: BulkWrite): Promise<BulkResponse> =>
    (await apiClient.post('/vulnerabilities/bulk/', body, OWNS_ERRORS)).data,
  getSlaPolicy: async (): Promise<SlaPolicyRow[]> => (await apiClient.get('/sla-policies/', OWNS_ERRORS)).data,
  patchSlaPolicy: async (priority: IssuePriority, days: number): Promise<SlaPolicyRow> =>
    (await apiClient.patch(`/sla-policies/${priority}/`, { days }, OWNS_ERRORS)).data,
  /**
   * Needs `organization_membership:view`; analysts may get a 403 (§7.5). The
   * pickers degrade to "restricted" on that, so the refusal is owned here:
   * no global toast, no forbidden redirect.
   */
  getMembers: async (orgId: string): Promise<IssueUser[]> => {
    const { data } = await apiClient.get(`/organizations/${orgId}/members/`, {
      ...OWNS_ERRORS,
      params: { page_size: 100 },
    })
    const rows: unknown[] = Array.isArray(data) ? data : (data?.results ?? [])
    return rows
      .map((m) => (m as { user?: IssueUser }).user ?? (m as IssueUser))
      .filter((u): u is IssueUser => !!u && typeof u.id === 'string' && typeof u.email === 'string')
      .map((u) => ({ id: u.id, email: u.email }))
  },

  /* Hidden-but-kept (§7): no backend route today. */
  patch: async (id: string, body: IssuePatch) =>
    (await apiClient.patch(`/vulnerabilities/${id}/`, body, OWNS_ERRORS)).data,
  getViews: async (): Promise<SavedViewsResponse> => (await apiClient.get('/issues/views/')).data,
  createView: async (name: string, query: string): Promise<SavedView> =>
    (await apiClient.post('/issues/views/', { name, query })).data,
  patchView: async (id: string, body: { name?: string; query?: string }): Promise<SavedView> =>
    (await apiClient.patch(`/issues/views/${id}/`, body)).data,
  deleteView: async (id: string): Promise<void> => {
    await apiClient.delete(`/issues/views/${id}/`)
  },
  shareView: async (id: string, shared: boolean): Promise<SavedView> =>
    (await apiClient.post(`/issues/views/${id}/${shared ? 'share' : 'unshare'}/`, {})).data,
}
